/**
 * Desktop studio chrome. Keep the workspace identity visible while the
 * viewer switches between individual scenes and finished videos.
 */
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { openFileExternally } from '@/constants/utils/files';
import { buildVideoProject } from '@/constants/utils/video_project';
import VideoProjectViewer from '@/components/Files/VideoProjectViewer';

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    backgroundColor: colors.solid_color_primary,
  },
  header: {
    minHeight: 68,
    paddingLeft: 14,
    paddingRight: 10,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.solid_color_block,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  identityIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  action: {
    minWidth: 34,
    height: 36,
    borderRadius: 8,
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  labeledAction: {
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  divider: {
    width: 1,
    height: 18,
    marginHorizontal: 4,
    backgroundColor: colors.border_color_secondary,
  },
  body: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
});

function HeaderAction({ label, text, icon, onPress, styles, colors, disabled }) {
  return (
    <Tooltip label={label} side="bottom">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled: !!disabled }}
        disabled={disabled}
        onPress={onPress}
        style={({ hovered }) => [
          styles.action,
          text && styles.labeledAction,
          hovered && !disabled && { backgroundColor: colors.solid_color_tertiary },
          disabled && { opacity: 0.35 },
        ]}
      >
        <Icon name={icon} size={16} color={colors.text_color_primary} />
        {text ? <Text size="s" weight="medium">{text}</Text> : null}
      </Pressable>
    </Tooltip>
  );
}

export default function VideoProjectPreviewPanel({
  file,
  artifacts,
  files,
  scriptText,
  taskId,
  conversationId,
  selectedId,
  selectionRequest,
  onClose,
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const project = useMemo(
    () => buildVideoProject({ file, artifacts, files, scriptText, selectedId }),
    [file, artifacts, files, scriptText, selectedId],
  );
  const mediaScope = useMemo(() => ({
    id: file?.id, legacyId: file?._id, path: file?.path, name: file?.name, originalName: file?.originalName,
    cdnPath: file?.cdnPath, url: file?.url, selectedId, selectionRequest, taskId, conversationId,
  }), [
    file?.id, file?._id, file?.path, file?.name, file?.originalName, file?.cdnPath, file?.url,
    selectedId, selectionRequest, taskId, conversationId,
  ]);
  const currentMediaScope = useRef(mediaScope);
  useLayoutEffect(() => {
    currentMediaScope.current = mediaScope;
  }, [mediaScope]);
  const [reportedMedia, setReportedMedia] = useState(null);
  const onActiveMediaChange = useCallback((media) => {
    if (currentMediaScope.current !== mediaScope) return;
    setReportedMedia({ scope: mediaScope, media: media || null });
  }, [mediaScope]);
  // Undefined means this selection has not reported yet. Explicit null means
  // its preview has no media (for example, an image still being generated),
  // so the header must not fall back to a different video from the project.
  const activeMedia = reportedMedia?.scope === mediaScope ? reportedMedia.media : undefined;
  const source = activeMedia === undefined
    ? file?.cdnPath || file?.url || project.film?.url
    : activeMedia?.url;
  const downloadName = activeMedia === undefined
    ? file?.name || file?.originalName || project.film?.name || 'video.mp4'
    : activeMedia?.name || (activeMedia?.kind === 'image' ? 'image.png' : 'video.mp4');
  const downloadLabel = activeMedia?.kind === 'image' ? 'Download image' : 'Download video';
  const openLabel = activeMedia?.kind === 'image' ? 'Open image in new tab' : 'Open video in new tab';

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.identityIcon}>
          <Icon name="clapperboard" size={16} color={colors.text_color_primary} />
        </View>
        <View style={styles.titleBlock}>
          <Text numberOfLines={1} weight="semibold">Video studio</Text>
          <Text numberOfLines={1} size="xs" color="secondary">
            {project.film ? project.title : 'Bring your story to life'}
          </Text>
        </View>
        <View style={styles.actions}>
          <HeaderAction
            label={openLabel}
            icon="arrow-up-right-from-square"
            disabled={!source}
            onPress={() => source && openFileExternally({
              ...file,
              name: downloadName,
              cdnPath: source,
              url: source,
            })}
            styles={styles}
            colors={colors}
          />
          <HeaderAction
            label={downloadLabel}
            text="Download"
            icon="arrow-down-to-line"
            disabled={!source}
            onPress={() => source && downloadMedia(source, downloadName)}
            styles={styles}
            colors={colors}
          />
          {onClose ? (
            <>
              <View style={styles.divider} />
              <HeaderAction
                label="Close video studio"
                icon="xmark"
                onPress={onClose}
                styles={styles}
                colors={colors}
              />
            </>
          ) : null}
        </View>
      </View>
      <View style={styles.body}>
        <VideoProjectViewer
          key={`${taskId || ''}:${conversationId || ''}`}
          file={file}
          artifacts={artifacts}
          files={files}
          scriptText={scriptText}
          taskId={taskId}
          conversationId={conversationId}
          selectedId={selectedId}
          selectionRequest={selectionRequest}
          onActiveMediaChange={onActiveMediaChange}
        />
      </View>
    </View>
  );
}
